import * as z from 'zod'
import { repoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import { useReplicacheQueryV2 } from './use-replicache-query'
import { useRepoReplicache } from './use-repo-replicache'
import {
  Branch,
  Commit,
  branchSchema,
  contentRequestSchema,
  isMergeCommit,
} from '@basehub/data/replicache/index'
import { useBranchMergeDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/branch/_hooks/use-merge-diff'
import { compareAsc } from 'date-fns'
import { useActiveSpaceId } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { useQuery } from '@tanstack/react-query'
import { invariant } from '@epic-web/invariant'

export const useBranches = (opts?: {
  status?: 'open' | 'closed'
  authorId?: string
  archived?: boolean
  baseBranchId?: string
  includeInlineSuggestions?: boolean
  includePlaygrounds?: boolean
}) => {
  const { isLoading: isLoadingReplicache, data: repoReplicacheData } =
    useRepoReplicache()
  const spaceId = useActiveSpaceId() ?? repoReplicacheData?.spaceId
  const replicache = repoReplicacheData?.replicache
  const replicacheQueryEnabled = !isLoadingReplicache && Boolean(replicache)

  const replicacheQuery = useReplicacheQueryV2({
    key: ['branches', repoReplicacheData?.spaceId, 'replicache'],
    replicache: repoReplicacheData?.replicache,
    fetcher: async (tx) => {
      const branches = await repoSpaceMutators.listBranches(tx)

      const branchesWithContentRequests = await Promise.all(
        branches.map(async (branch) => {
          const contentRequests = await Promise.all(
            branch.contentRequestIds?.map(
              async (crId) =>
                await repoSpaceMutators.getContentRequest(tx, crId)
            ) ?? []
          )

          if (!branch || !contentRequests) return null

          return { branch, contentRequests: contentRequests.filter(Boolean) }
        })
      )

      return branchesWithContentRequests.filter(Boolean)
    },
    enabled: replicacheQueryEnabled,
  })

  const fallbackQueryEnabled =
    !isLoadingReplicache &&
    !replicacheQueryEnabled &&
    !!spaceId &&
    !replicacheQuery.data

  const fallback = useQuery({
    queryKey: ['branches', repoReplicacheData?.spaceId, 'fallback'],
    queryFn: async () => {
      invariant(spaceId, 'spaceId is undefined, this should not happen.')
      const branchesResponse = await fetch(
        `/api/ad-hoc/get-branches?spaceId=${encodeURIComponent(spaceId)}`
      )
      const branchesWithContentRequests = z
        .array(
          z.object({
            branch: branchSchema,
            contentRequests: z.array(contentRequestSchema),
          })
        )
        .parse(await branchesResponse.json())

      return branchesWithContentRequests.filter(Boolean)
    },
    staleTime: Infinity,
    enabled: fallbackQueryEnabled,
  })

  const branchesQuery = replicacheQueryEnabled ? replicacheQuery : fallback

  if (!branchesQuery.data) return branchesQuery
  const filteredBranches: Branch[] = []

  branchesQuery.data.forEach(({ branch, contentRequests }) => {
    if (branch.isInlineSuggestion && !opts?.includeInlineSuggestions) {
      return
    }

    if (!!branch.playgroundId && !opts?.includePlaygrounds) {
      return
    }

    if (!opts) {
      filteredBranches.push(branch)
      return
    }

    if (opts?.archived !== undefined) {
      if (opts.archived && !branch.archivedAt) return
      else if (!opts.archived && branch.archivedAt) return
    }

    if (!opts?.authorId && branch.isDefault && !opts.baseBranchId) {
      filteredBranches.push(branch)
      return
    }

    const shouldInclude = contentRequests.some((cr) => {
      if (opts.status !== undefined) {
        if (opts.status === 'open') {
          if (cr?.deletedAt) return
        } else {
          if (!cr?.deletedAt) return
        }
      }

      if (opts.authorId !== undefined) {
        if (cr?.authorId !== opts.authorId) return
      }

      if (opts.baseBranchId !== undefined) {
        if (!cr || cr?.baseBranchId !== opts.baseBranchId) return
      }

      return true
    })

    if (shouldInclude) filteredBranches.push(branch)
  })

  return { ...branchesQuery, data: filteredBranches }
}

export const useActiveBranches = (branch: Branch | null) => {
  const { data } = useRepoReplicache()
  const { data: branchesData } = useBranches({
    archived: false,
    status: 'open',
    baseBranchId: branch?.id,
  })
  const branches = branchesData ?? []

  return useReplicacheQueryV2({
    key: ['active-branches', branch?.id, branches],
    replicache: data?.replicache,
    enabled: !!branches && !!branch,
    fetcher: async (tx) => {
      if (!branch) return []
      const mainLineageCache = new Map<Commit['id'], Commit>()

      const nonMergedBranches = await Promise.all(
        branches.map(async (b) => {
          let lastNonMergeCommit = b.headCommit
          if (!lastNonMergeCommit) return null

          // Handle case where headCommit might not be available
          if (typeof lastNonMergeCommit === 'string') {
            lastNonMergeCommit = await repoSpaceMutators.getCommit(
              tx,
              lastNonMergeCommit
            )
            if (!lastNonMergeCommit) return null
          }

          if (isMergeCommit(lastNonMergeCommit)) {
            if (!lastNonMergeCommit.parentCommitId) return null

            lastNonMergeCommit = await repoSpaceMutators.getCommit(
              tx,
              lastNonMergeCommit.parentCommitId
            )
            if (!lastNonMergeCommit) return null
          }

          // Branch was created after last commit. It's not merged
          if (
            compareAsc(
              new Date(b.createdAt),
              new Date(lastNonMergeCommit.createdAt)
            ) >= 0
          ) {
            return b
          }

          let thisCommitIsInBaseBranch = false
          let currentCommitId = branch.headCommitId

          while (currentCommitId) {
            const currentCommit =
              mainLineageCache.get(currentCommitId) ??
              (await repoSpaceMutators.getCommit(tx, currentCommitId))

            if (!currentCommit) break
            mainLineageCache.set(currentCommit.id, currentCommit)

            if (
              currentCommit.mergeParentCommitId === lastNonMergeCommit.id ||
              compareAsc(
                new Date(b.createdAt),
                new Date(currentCommit.createdAt)
              ) >= 0
            ) {
              thisCommitIsInBaseBranch = true
              break
            }

            currentCommitId = currentCommit.parentCommitId
          }

          return thisCommitIsInBaseBranch ? null : b
        })
      )

      return nonMergedBranches.filter((b): b is Branch => b !== null)
    },
  })
}

export const useMergedBranches = (branch: Branch | null) => {
  const { data } = useRepoReplicache()
  const { data: branchesData } = useBranches({
    archived: false,
    status: 'open',
    baseBranchId: branch?.id,
  })
  const { diffCommitLineage } = useBranchMergeDiff({
    branch,
  })
  const branches = branchesData ?? []

  return useReplicacheQueryV2({
    key: ['merged-branches', branch?.id, branches, diffCommitLineage],
    replicache: data?.replicache,
    enabled: !!branches.length && !!branch,
    fetcher: async (tx) => {
      if (!branch) return []
      const mainLineageCache = new Map<Commit['id'], Commit>()

      const mergedBranches = await Promise.all(
        branches.map(async (b) => {
          let lastNonMergeCommit = b.headCommit
          if (!lastNonMergeCommit) return null

          // Handle case where headCommit might not be available
          if (typeof lastNonMergeCommit === 'string') {
            lastNonMergeCommit = await repoSpaceMutators.getCommit(
              tx,
              lastNonMergeCommit
            )
            if (!lastNonMergeCommit) return null
          }

          if (isMergeCommit(lastNonMergeCommit)) {
            if (!lastNonMergeCommit.parentCommitId) return null

            lastNonMergeCommit = await repoSpaceMutators.getCommit(
              tx,
              lastNonMergeCommit.parentCommitId
            )
            if (!lastNonMergeCommit) return null
          }

          // Branch was created after last commit. It's not merged
          if (
            compareAsc(
              new Date(b.createdAt),
              new Date(lastNonMergeCommit.createdAt)
            ) >= 0
          ) {
            return null
          }

          let thisCommitIsInBaseBranch = false
          let currentCommitId = branch.headCommitId

          while (currentCommitId) {
            const currentCommit =
              mainLineageCache.get(currentCommitId) ??
              (await repoSpaceMutators.getCommit(tx, currentCommitId))

            if (!currentCommit) break
            mainLineageCache.set(currentCommit.id, currentCommit)

            if (
              currentCommit.mergeParentCommitId === lastNonMergeCommit.id ||
              compareAsc(
                new Date(b.createdAt),
                new Date(currentCommit.createdAt)
              ) >= 0
            ) {
              thisCommitIsInBaseBranch = true
              break
            }

            currentCommitId = currentCommit.parentCommitId
          }

          return thisCommitIsInBaseBranch ? b : null
        })
      )

      return mergedBranches.filter((b): b is Branch => b !== null)
    },
  })
}
